import {
  Bloc,
  ConstantBackoff,
  ILoadingBloc,
  WebsocketBloc
} from "@kernel/frontend-react-app";
import { RoomState, defaultRoomState } from "./RoomState";
import { LocalVideoManager } from "modules/local";
import { RoomVideoBloc } from "./RoomVideoBloc";
import { ActiveDevice, Room, Stream } from "@domain";
import { RoomSettingsBloc } from "./RoomSettingsBloc";
import { RoomStatsBloc } from "./RoomStatsBloc";
import {
  PinParticipantInteractor,
  UnPinParticipantInteractor
} from "./interactors";
import { MainBloc, NotificationHandlerProps } from "../main";
import { RoomStreamingBloc, UserTypeFilterConfig } from "./RoomStreamingBloc";
import { UniqueEntityId } from "@kernel/ddd";
import { UserType } from "./UserType";

export class RoomBloc
  extends Bloc<RoomState>
  implements LocalVideoManager, ILoadingBloc
{
  public readonly roomVideoBloc: RoomVideoBloc;
  public readonly roomSettingsBloc: RoomSettingsBloc;
  public readonly roomStatsBloc: RoomStatsBloc;
  public readonly roomStreamingBloc: RoomStreamingBloc;
  private mainBloc: MainBloc | null = null;
  private wsBloc: WebsocketBloc;
  public constructor() {
    super(defaultRoomState);
    this.roomVideoBloc = RoomVideoBloc.create(this);
    this.roomSettingsBloc = RoomSettingsBloc.create(this);
    this.roomStatsBloc = RoomStatsBloc.create(this);
    this.roomStreamingBloc = RoomStreamingBloc.create(this);
    this.wsBloc = WebsocketBloc.create(this, {
      backoff: new ConstantBackoff(500)
    });
  }

  public setMainBloc(mainBloc: MainBloc): void {
    this.mainBloc = mainBloc;
    this.roomStatsBloc.setMainBloc(mainBloc);
    this.roomStreamingBloc.setConfig(mainBloc.state.config!.webrtc);
  }

  public getMainBloc(): MainBloc {
    if (this.mainBloc === null) throw new Error("MainBloc is null");
    return this.mainBloc;
  }

  public startLoading(message: string): void {
    const mainBloc = this.getMainBloc();
    mainBloc.startLoading(message);
  }

  public stopLoading(): void {
    const mainBloc = this.getMainBloc();
    mainBloc.stopLoading();
  }

  public handleNotification(props: NotificationHandlerProps): void {
    const mainBloc = this.getMainBloc();
    mainBloc.handleNotification(props);
  }

  /** WEBSOCKET BLOC PROXY */
  public async connectWebsocket(wsUrl: string, wsProtocol?: string) {
    await this.wsBloc.connect(wsUrl, { wsProtocol });
  }

  public async change(
    videoElement: HTMLVideoElement,
    type: UserType,
    selectedDevice?: ActiveDevice | undefined,
    roomName?: string,
    displayName?: string,
    isScreenSharing?: boolean
  ): Promise<void> {
    if (!selectedDevice) throw new Error("SelectedDevice is not set");
    if (!roomName) throw new Error("RoomName is not set");
    await this.roomVideoBloc.change(
      videoElement,
      selectedDevice,
      roomName,
      type,
      displayName,
      isScreenSharing
    );
    const room = this.state.room || Room.createEmpty(roomName);
    this.roomStreamingBloc.changeState({
      room,
      statsRecorder: this.roomStreamingBloc.state.statsRecorder,
      userTypeFilter: this.roomStreamingBloc.state.userTypeFilter
    });
  }

  public changeLocalParticipant(displayName: string): void {
    this.roomStreamingBloc.changeLocalParticipant(displayName);
  }

  public setUserTypeFilter(config: UserTypeFilterConfig): void {
    this.roomStreamingBloc.setUserTypeFilter(config);
  }

  public pinParticipant(participantId: UniqueEntityId): void {
    const room = this.state.room;
    if (room === null) return;
    const pinParticipant = PinParticipantInteractor.create();
    this.changeState({
      ...this.state,
      room: pinParticipant.execute({
        room,
        participantId
      })
    });
  }

  public unpinParticipant(participantId: UniqueEntityId): void {
    const room = this.state.room;
    if (room === null) return;
    const unpinParticipant = UnPinParticipantInteractor.create();
    this.changeState({
      ...this.state,
      room: unpinParticipant.execute({
        room,
        participantId
      })
    });
  }

  public restart(type: "audio" | "video"): void {
    this.roomVideoBloc.restart(type);
  }

  public stop(type: "audio" | "video"): void {
    this.roomVideoBloc.stop(type);
  }

  public async replaceScreenTrack(mediaStream: MediaStream): Promise<void> {
    const room = this.state.room;
    if (room === null) return;
    if (room.localParticipant === null) return;

    const stream = Stream.create({
      mediaStream,
      local: true,
      sourceType: "screen"
    });

    room.localParticipant.stream = stream;

    const remoteParticipants = room.getRemoteParticipants();
    for (const participant of remoteParticipants.getItems()) {
      await participant.peer.replaceTrack(mediaStream);
    }
  }

  public static create(): RoomBloc {
    return new RoomBloc();
  }
}
